import { ReactElement, useEffect, useState } from 'react';
import {
  BrandImpactData,
  IncrementalRoasTakeaways
} from '@blisspointmedia/bpm-types/dist/BrandEquity';
import { MedianOrMean } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { BrandEquityHeader } from '../BrandEquityHeader';
import { formatModelOverviewOutcomeVariable } from '../BrandEquityUtils';
import { isQueryVolumeMetric } from '../metricNormalization';
import { InvestToIncrease } from './InvestToIncrease';
import { ModelInsights } from './ModelInsights';
import { Overview } from './Overview';
import { PaidMediaImpact } from './PaidMediaImpact';

interface BrandImpactProps {
  company: string;
  groupByMetric: string;
  groupByLevel: string;
  groupByMetricOptions: {
    label: string;
    value: string;
    s3Path: string;
    client_facing: boolean;
    kpi: string;
  }[];
  brandImpactData: BrandImpactData;
  incrementalRoasTakeAways: IncrementalRoasTakeaways;
  queryVolumeLongLabel: string;
  queryVolumeShortLabel: string;
  centralMeasure: MedianOrMean;
}

export const BrandImpact = ({
  company,
  groupByMetric,
  groupByLevel,
  brandImpactData,
  incrementalRoasTakeAways,
  groupByMetricOptions,
  queryVolumeLongLabel,
  queryVolumeShortLabel,
  centralMeasure
}: BrandImpactProps): ReactElement => {
  const {
    weeklyBrandHealthTimeSeries,
    modelOverview,
    weeklyBrandHealthAreaChartSpend,
    implicationsData,
    paidMediaAreaChart,
    paidMediaBarChart,
    spendAndEffectBarChart,
    nextBestDollarBarChart,
    saturationData,
    decayData
  } = brandImpactData;
  const [isQv, setGqv] = useState<boolean>(true);
  useEffect(() => {
    const checkKpi = groupByMetricOptions.some(item => isQueryVolumeMetric(item.kpi));
    setGqv(checkKpi);
  }, [groupByMetricOptions]);

  return (
    <>
      <BrandEquityHeader />
      {Object.keys(modelOverview).length > 0 &&
        weeklyBrandHealthTimeSeries.length > 0 &&
        Object.keys(weeklyBrandHealthAreaChartSpend).length > 0 &&
        modelOverview[groupByMetric] &&
        Boolean(weeklyBrandHealthAreaChartSpend[groupByMetric]?.length) && (
          <Overview
            groupByMetric={groupByMetric}
            groupByLevel={groupByLevel}
            weeklyBrandHealthTimeSeries={weeklyBrandHealthTimeSeries}
            weeklyBrandHealthAreaChartSpend={weeklyBrandHealthAreaChartSpend}
            modelOverviewAll={modelOverview}
            incrementalRoasTakeaways={incrementalRoasTakeAways}
            isQv={isQv}
            queryVolumeLongLabel={queryVolumeLongLabel}
            queryVolumeShortLabel={queryVolumeShortLabel}
          />
        )}
      {Object.keys(implicationsData).length > 0 &&
        Boolean(paidMediaAreaChart[groupByMetric]?.length) && (
          <PaidMediaImpact
            groupByMetric={formatModelOverviewOutcomeVariable(groupByMetric)}
            paidMediaImpactAreaChart={paidMediaAreaChart[groupByMetric]}
            paidMediaImpactBarChart={paidMediaBarChart}
            implicationsData={implicationsData}
            isQv={isQv}
            queryVolumeLongLabel={queryVolumeLongLabel}
            queryVolumeShortLabel={queryVolumeShortLabel}
          />
        )}
      {Object.keys(nextBestDollarBarChart).length > 0 &&
        Object.keys(spendAndEffectBarChart).length > 0 &&
        Boolean(spendAndEffectBarChart[groupByMetric]?.length) && (
          <InvestToIncrease
            groupByMetric={formatModelOverviewOutcomeVariable(groupByMetric)}
            unGroupedNextBestThousandDollars={nextBestDollarBarChart[groupByMetric]}
            unGroupedSpendAndEffect={spendAndEffectBarChart[groupByMetric]}
            groupByLevel={groupByLevel}
            queryVolumeLongLabel={queryVolumeLongLabel}
            queryVolumeShortLabel={queryVolumeShortLabel}
          />
        )}
      {Object.keys(decayData).length > 0 &&
        Object.keys(saturationData).length > 0 &&
        Boolean(saturationData[groupByMetric]?.length) && (
          <ModelInsights
            company={company}
            groupByMetric={groupByMetric}
            saturationDataUnformatted={saturationData[groupByMetric]}
            decayDataUnformatted={decayData[groupByMetric]}
            queryVolumeShortLabel={queryVolumeShortLabel}
            centralMeasure={centralMeasure}
          />
        )}
    </>
  );
};
